/**
 * Per-device view state: which rails are collapsed, and which folders are open.
 *
 * None of this belongs to the vault. A rail collapsed on a laptop says nothing
 * about the same account on a phone, and syncing it would fight the user on
 * whichever device it reached second. It lives in `localStorage` instead.
 *
 * Every read and write is guarded because `localStorage` is not merely empty in
 * a private window or with site data blocked — the property access itself
 * throws. An unguarded read there takes the whole shell down at start-up, which
 * is a blank page rather than a vault that has forgotten a preference.
 */

/** Which rails the reader has put away. Absent storage means both are showing. */
export interface RailState {
  readonly navCollapsed: boolean;
  readonly contextCollapsed: boolean;
}

export const RAILS_SHOWING: RailState = { navCollapsed: false, contextCollapsed: false };

const RAIL_KEY = "recallvault.rails";

/**
 * Expanded folders are capped. A reader who browses a whole migration would
 * otherwise grow an unbounded key that is rewritten on every disclosure click.
 */
const FOLDER_LIMIT = 300;

function readValue(store: Storage | undefined, key: string): string | undefined {
  try {
    return store?.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeValue(store: Storage | undefined, key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    // Private windows and quota exhaustion both land here. The preference is a
    // convenience; losing it must never interrupt what the reader was doing.
  }
}

export function readRailState(store: Storage | undefined): RailState {
  const raw = readValue(store, RAIL_KEY);
  if (raw === undefined) return RAILS_SHOWING;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return RAILS_SHOWING;
    const record = parsed as Record<string, unknown>;
    return {
      navCollapsed: record["navCollapsed"] === true,
      contextCollapsed: record["contextCollapsed"] === true,
    };
  } catch {
    return RAILS_SHOWING;
  }
}

export function writeRailState(store: Storage | undefined, state: RailState): void {
  writeValue(store, RAIL_KEY, JSON.stringify(state));
}

function folderKey(accountId: string): string {
  return `recallvault.folders:${accountId}`;
}

export function readExpandedFolders(store: Storage | undefined, accountId: string): Set<string> {
  const raw = readValue(store, folderKey(accountId));
  if (raw === undefined) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((item): item is string => typeof item === "string").slice(0, FOLDER_LIMIT));
  } catch {
    return new Set();
  }
}

export function writeExpandedFolders(store: Storage | undefined, accountId: string, folders: ReadonlySet<string>): void {
  writeValue(store, folderKey(accountId), JSON.stringify([...folders].slice(0, FOLDER_LIMIT)));
}

const RELEASE_KEY = "recallvault.dismissed-release";

/**
 * Which release the reader has already waved away. Dismissing an update offer
 * must survive a re-render, and nagging on every navigation is worse than the
 * stale shell it warns about.
 */
export function readDismissedRelease(store: Storage | undefined): string {
  return readValue(store, RELEASE_KEY) ?? "";
}

export function writeDismissedRelease(store: Storage | undefined, release: string): void {
  writeValue(store, RELEASE_KEY, release);
}
